import { useEffect, useRef } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useDispatch, useSelector } from 'react-redux';
import axios from 'axios';
import Toast from '../components/Toast';
import { LOGIN_PATH } from '../components/LegalLinks';
import { jsonGet, jsonPost } from '../api/jsonRequest';
import { getAuthConfig } from '../mixins/ApiConfig';
import { resetSessionExpiryGuard } from '../mixins/authSession';
import { usePermissionStore } from '../stores/permissionStore';

export async function fetchAndStoreUserSession(userId, dispatch) {
    const config = getAuthConfig();
    try {
        const userCompany = await axios.get(`/api/Company/GetAllCompaniesForUser/${userId}`, config);
        dispatch({ type: 'USER_COMPANIES', payload: userCompany.data });
    } catch (error) {
        console.log(`Error getting companies: ${error}`);
    }
    try {
        const userInfo = await axios.get(`/api/Account/GetUserInfo/${userId}`, config);
        dispatch({ type: 'SET_USER', payload: userInfo.data });
    } catch (error) {
        console.log(`Error grabbing user info: ${error}`);
    }
    try {
        const userRole = await axios.get(`/api/Account/GrabUserRole/${userId}`, config);
        dispatch({ type: 'SET_USER_ROLE', payload: userRole.data });
    } catch (error) {
        console.log(`Error grabbing user role: ${error}`);
    }
}

export async function fetchAndStorePermissions() {
    const { setPermissions, setPermissionsError } = usePermissionStore.getState();
    try {
        const data = await jsonGet('/api/Permissions/Me');
        setPermissions(data);
        return data;
    } catch (error) {
        console.log('Error fetching permissions:', error);
        setPermissionsError();
        return null;
    }
}

/** Clears loading/error and retries /Permissions/Me into the permission store. */
export function useRetryPermissions() {
    const beginPermissionsReload = usePermissionStore((state) => state.beginPermissionsReload);

    return async () => {
        beginPermissionsReload();
        await fetchAndStorePermissions();
    };
}

/**
 * Loads companies, profile, role, and permissions when authenticated.
 * Tracks bootstrap per user id in the Zustand store so login and remounts do not double-fetch.
 */
export function useSessionBootstrap() {
    const dispatch = useDispatch();
    const authenticated = useSelector((state) => state.UserReducer.isAuthenticated);
    const userId = authenticated?.id;
    const sessionBootstrappedForUserId = usePermissionStore(
        (state) => state.sessionBootstrappedForUserId
    );
    const setSessionBootstrappedForUserId = usePermissionStore(
        (state) => state.setSessionBootstrappedForUserId
    );
    const inFlightUserIdRef = useRef(null);

    // Sync session profile/permissions when auth appears outside React (sessionStorage restore).
    useEffect(() => {
        if (!userId) {
            return;
        }

        if (sessionBootstrappedForUserId === userId || inFlightUserIdRef.current === userId) {
            return;
        }

        let cancelled = false;
        inFlightUserIdRef.current = userId;

        (async () => {
            await fetchAndStoreUserSession(userId, dispatch);
            await fetchAndStorePermissions();
            if (!cancelled) {
                setSessionBootstrappedForUserId(userId);
            }
            if (inFlightUserIdRef.current === userId) {
                inFlightUserIdRef.current = null;
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [userId, sessionBootstrappedForUserId, dispatch, setSessionBootstrappedForUserId]);
}

export function useLogin() {
    const dispatch = useDispatch();
    const setSessionBootstrappedForUserId = usePermissionStore(
        (state) => state.setSessionBootstrappedForUserId
    );

    return useMutation({
        mutationFn: async ({ UserName, Password }) => {
            dispatch({ type: 'USER_LOADING', payload: true });
            const user = await axios.post('/api/Account/GetToken', {
                Username: UserName,
                Password,
            }, {
                headers: {
                    Accept: 'application/json',
                    'Content-Type': 'application/json',
                    'Access-Control-Allow-Origin': 'https://*, http://*',
                },
            });
            if (user.status !== 200) {
                throw new Error('Login failed');
            }
            return user.data;
        },
        onSuccess: async (data) => {
            sessionStorage.setItem('auth', JSON.stringify(data));
            sessionStorage.setItem('lastTab', '4');
            resetSessionExpiryGuard();
            dispatch({ type: 'IS_AUTHENTICATED', payload: data });
            await fetchAndStoreUserSession(data.id, dispatch);
            await fetchAndStorePermissions();
            setSessionBootstrappedForUserId(data.id);
            dispatch({ type: 'USER_LOADING', payload: false });
        },
        onError: () => {
            Toast.fire({
                icon: 'error',
                title: 'Oops, something went wrong, double check your username and or password.',
            });
            dispatch({ type: 'USER_LOADING', payload: false });
        },
    });
}

export function useLogout() {
    const dispatch = useDispatch();
    const queryClient = useQueryClient();
    const clearPermissions = usePermissionStore((state) => state.clearPermissions);

    return () => {
        dispatch({ type: 'IS_AUTHENTICATED', payload: false });
        sessionStorage.removeItem('auth');
        queryClient.clear();
        clearPermissions();
        window.location.reload();
    };
}

export function useRegisterUser() {
    return useMutation({
        mutationFn: async ({ payload, navigate }) => {
            try {
                const res = await jsonPost('/api/Account/RegisterNewUser', payload);
                if (res.status !== 200) {
                    throw new Error(typeof res.data === 'string' && res.data
                        ? res.data
                        : 'Register failed');
                }
                return { data: res.data, navigate };
            } catch (error) {
                const serverMessage = error?.response?.data;
                const message = typeof serverMessage === 'string' && serverMessage
                    ? serverMessage
                    : 'Oops, something went wrong or Email may already be in use.';
                throw new Error(message);
            }
        },
        onSuccess: ({ data, navigate }) => {
            sessionStorage.setItem('pickUser', JSON.stringify(data ?? null));
            Toast.fire({
                icon: 'success',
                title: 'Successfully registered account, next please verify email address.',
            });
            navigate(LOGIN_PATH, { replace: true });
        },
        onError: (error) => {
            Toast.fire({
                icon: 'error',
                title: error?.message || 'Oops, something went wrong or Email may already be in use.',
            });
        },
    });
}

export function useConfirmEmail() {
    return useMutation({
        mutationFn: async ({ token, navigate }) => {
            const res = await axios.get(`/api/Account/ConfirmEmail/${token}`);
            if (res.status !== 200) {
                throw new Error('Confirm failed');
            }
            return navigate;
        },
        onSuccess: (navigate) => {
            Toast.fire({ icon: 'success', title: 'Please Login to continue.' });
            navigate(LOGIN_PATH);
        },
    });
}

export function useRequestPassword() {
    return useMutation({
        mutationFn: async ({ payload, navigate }) => {
            const res = await axios.post('/api/Account/RequestPassword', payload);
            if (res.status !== 200) {
                throw new Error('Request failed');
            }
            return navigate;
        },
        onSuccess: (navigate) => {
            Toast.fire({
                icon: 'success',
                title: 'Check your email to recover password.',
            });
            navigate(LOGIN_PATH);
        },
    });
}

export function useForgotPassword() {
    return useMutation({
        mutationFn: async ({ payload, navigate }) => {
            const res = await axios.post('/api/Account/ForgotPassword', payload);
            if (res.status !== 200) {
                throw new Error('Reset failed');
            }
            return navigate;
        },
        onSuccess: (navigate) => {
            Toast.fire({
                icon: 'success',
                title: 'Password successfully updated.',
            });
            navigate(LOGIN_PATH);
        },
        onError: () => {
            Toast.fire({
                icon: 'error',
                title: 'Please Check Your Password',
            });
        },
    });
}

export function useUpdateAgreement() {
    const dispatch = useDispatch();

    return useMutation({
        mutationFn: async (payload) => {
            const config = getAuthConfig();
            const res = await axios.post('api/Account/UpdatePDFStatus', payload, config);
            if (res.status !== 200) {
                throw new Error('Agreement update failed');
            }
            const userInfo = await axios.get(`/api/Account/GetUserInfo/${payload.Id}`, config);
            return { user: userInfo.data, payload };
        },
        onSuccess: ({ user, payload }) => {
            dispatch({ type: 'SET_USER', payload: user });
            Toast.fire({ icon: 'success', title: 'Agreement Accepted.' });
            if (payload.hasAcceptedUploadPolicy === 1) {
                window.location.reload();
            }
        },
        onError: () => {
            Toast.fire({
                icon: 'error',
                title: 'Agreement status not updated, please try again.',
            });
        },
    });
}

export function useUpdateUserInfo() {
    const dispatch = useDispatch();

    return useMutation({
        mutationFn: async ({ userId, user }) => {
            const config = getAuthConfig();
            const res = await axios.post(`/api/Account/UpdateUserInfo/${userId}`, user, config);
            if (res.status !== 200) {
                throw new Error('Update failed');
            }
            const userInfo = await axios.get(`/api/Account/GetUserInfo/${userId}`, config);
            return userInfo.data;
        },
        onSuccess: (user) => {
            dispatch({ type: 'SET_USER', payload: user });
            Toast.fire({ icon: 'success', title: 'Account Updated.' });
        },
        onError: () => {
            Toast.fire({
                icon: 'error',
                title: 'Account not updated check your entry and try again.',
            });
        },
    });
}

export function useUpdateUserPassword() {
    const dispatch = useDispatch();

    return useMutation({
        mutationFn: async ({ userId, user }) => {
            const config = getAuthConfig();
            const res = await axios.post(`/api/Account/UpdateUserPassword/${userId}`, user, config);
            if (res.status !== 200) {
                throw new Error('Update failed');
            }
            const userInfo = await axios.get(`/api/Account/GetUserInfo/${userId}`, config);
            return userInfo.data;
        },
        onSuccess: (user) => {
            dispatch({ type: 'SET_USER', payload: user });
            Toast.fire({ icon: 'success', title: 'Password Updated.' });
        },
        onError: () => {
            Toast.fire({
                icon: 'error',
                title: 'Password not updated check your entry and try again.',
            });
        },
    });
}
